<template>
  <div class="form-group prompt-media-question">
    <div
      class="prompt-media"
      :class="{
        'prompt-media--loading': !isUsingFallback && !mediaLoaded && !mediaError && !youtubeVideoId,
        'prompt-media--fallback': isUsingFallback,
      }"
    >
      <div v-if="isUsingFallback" class="prompt-media__fallback" aria-hidden="true">
        <svg
          class="prompt-media__fallback-icon"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <path d="M21 15l-5-5L5 21" />
        </svg>
      </div>

      <template v-else-if="!mediaError">
        <img
          v-if="isImageMedia"
          :src="mediaUrl"
          :alt="questionText"
          class="prompt-media__image"
          loading="lazy"
          @load="onMediaLoad"
          @error="onMediaError"
        />

        <div v-else-if="isVideoMedia" class="prompt-media__video-wrap">
          <div
            v-if="youtubeVideoId"
            class="prompt-media__youtube"
            :style="youtubePosterStyle"
          >
            <youtube
              class="prompt-media__youtube-player"
              :video-id="youtubeVideoId"
              player-width="100%"
              player-height="100%"
              :player-vars="{ controls: 1, playsinline: 1, rel: 0 }"
            />
          </div>
          <video
            v-else
            class="prompt-media__video"
            controls
            playsinline
            preload="metadata"
            :src="mediaUrl"
            @loadeddata="onMediaLoad"
            @error="onMediaError"
          />
        </div>
      </template>
    </div>

    <div style="width: 100%" class="d-flex">
      <label
        :style="template.style.surveyBgColor ? { color: template.style.firstColor } : (survey.id == 552 ? { color: '#12b' } : { color: black })"
        class="title-bold mr-2"
        :for="hasOptions ? null : inputId"
      >
        {{ questionText }}
        <span v-if="mandatoryQuestion === false">(Opcional)</span>
      </label>
    </div>

    <p
      v-if="questionObs"
      :style="
        template.style.surveyBgColor
          ? { color: template.style.secondColor }
          : { color: black }
      "
      class="observation"
    >
      {{ questionObs }}
    </p>

    <input
      v-if="!hasOptions"
      ref="answerInput"
      type="text"
      class="form-control prompt-media-question__input"
      :id="inputId"
      :style="
        template.style.surveyBgColor
          ? {
              backgroundColor: template.style.surveyBgColor,
              color: template.style.surveyTextColor,
            }
          : { color: template.style.surveyTextColor }
      "
      :placeholder="getPlaceHolder"
      v-model="qtAnwser"
      v-on:keyup.enter="enterKey"
      v-on:keyup="allKeys"
    />

    <questionList
      v-else
      class="prompt-media-question__list"
      hideHeader
      :questionText="questionText"
      :questionObs="questionObs"
      :questionId="questionId"
      :options="options"
      :config="config"
      @QuestionAnwered="forwardQuestionAnswered"
      @AnswerChanged="forwardAnswerChanged"
    />
  </div>
</template>

<script>
import questionList from "./3_InputListQuestion.vue";

function extractYoutubeVideoId(url) {
  if (!url) return null;

  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./, "");

    if (host === "youtu.be") {
      return parsed.pathname.slice(1).split("/")[0] || null;
    }

    if (host.includes("youtube.com")) {
      if (parsed.pathname.startsWith("/embed/")) {
        return parsed.pathname.split("/")[2] || null;
      }
      return parsed.searchParams.get("v");
    }
  } catch (e) {
    return null;
  }

  return null;
}

function stripConfigQuotes(value) {
  const trimmed = String(value || "").trim();
  if (
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'"))
  ) {
    return trimmed.slice(1, -1).trim();
  }
  return trimmed;
}

function parseMediaFromConfig(config) {
  const result = { type: "", url: "" };
  if (!config) return result;

  const tokens = String(config).split(";");
  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i].trim();
    if (!token) continue;

    const sep = token.indexOf(":");
    if (sep <= 0) continue;

    const key = token.slice(0, sep).trim().toLowerCase();
    const value = stripConfigQuotes(token.slice(sep + 1));

    if (key === "mediatype") result.type = value.toLowerCase();
    else if (key === "mediaurl" || key === "media") result.url = value;
  }

  return result;
}

export default {
  name: "Survey-Prompt-Media",
  components: { questionList },
  data: () => ({
    qtAnwser: "",
    mediaLoaded: false,
    mediaError: false,
    useFallbackMedia: false,
  }),
  props: {
    questionText: { type: String },
    questionObs: { type: String, default: "" },
    questionId: { type: Number },
    placeHolder: { type: String },
    mandatoryQuestion: { type: Boolean },
    config: { type: String, default: "" },
    options: { type: Array, default: null },
  },
  computed: {
    hasOptions() {
      return Array.isArray(this.options) && this.options.length > 0;
    },
    inputId() {
      return `prompt-media-${this.questionId}`;
    },
    configMedia() {
      return parseMediaFromConfig(this.config);
    },
    mediaUrl() {
      return this.configMedia.url ? String(this.configMedia.url).trim() : "";
    },
    isUsingFallback() {
      return this.useFallbackMedia || !this.mediaUrl;
    },
    mediaType() {
      if (this.isUsingFallback) return "image";
      if (this.configMedia.type) return String(this.configMedia.type).toLowerCase();
      return "image";
    },
    isImageMedia() {
      return this.mediaType === "image";
    },
    isVideoMedia() {
      return !this.isUsingFallback && this.mediaType === "video";
    },
    youtubeVideoId() {
      if (!this.isVideoMedia) return null;
      return extractYoutubeVideoId(this.mediaUrl);
    },
    youtubePosterStyle() {
      if (!this.youtubeVideoId) return {};
      return {
        backgroundImage: `url('https://img.youtube.com/vi/${this.youtubeVideoId}/0.jpg')`,
      };
    },
    survey() {
      return this.$store.state.template.survey;
    },
    template() {
      return this.$store.state.template;
    },
    getPlaceHolder() {
      if (this.placeHolder) return this.placeHolder;
      return this.$t("type_answer_here");
    },
  },
  watch: {
    qtAnwser() {
      this.allKeys();
    },
    config() {
      this.resetMediaState();
    },
  },
  mounted() {
    if (!this.hasOptions) {
      this.qtAnwser = this.$store.getters.answerValue(this.questionId);
      this.focusInput();
    }
    if (this.youtubeVideoId || this.isUsingFallback) {
      this.mediaLoaded = true;
    }
  },
  methods: {
    resetMediaState() {
      this.mediaLoaded = false;
      this.mediaError = false;
      this.useFallbackMedia = false;
      if (!this.mediaUrl) this.mediaLoaded = true;
    },
    onMediaLoad() {
      this.mediaLoaded = true;
      this.mediaError = false;
    },
    onMediaError() {
      this.useFallbackMedia = true;
      this.mediaError = false;
      this.mediaLoaded = true;
    },
    focusInput() {
      const input = this.$refs.answerInput;
      if (input) input.focus();
    },
    isAnswerValid(answer) {
      const value = String(answer || "").trim();
      const isMandatory = this.mandatoryQuestion !== false;

      if (!isMandatory && value.length === 0) return true;
      return value.length > 2;
    },
    allKeys() {
      if (this.qtAnwser == undefined) this.qtAnwser = "";
      this.$emit("AnswerChanged", this.qtAnwser.trim(), this.questionId);
    },
    enterKey() {
      const answer = this.qtAnwser.trim();
      if (this.isAnswerValid(answer)) {
        this.$emit("QuestionAnwered", answer, this.questionId);
      } else {
        this.$emit(
          "ErrorOccured",
          "Informe corretamente: " + this.questionText,
          this.questionId
        );
      }
    },
    forwardQuestionAnswered(answer, questionId) {
      this.$emit("QuestionAnwered", answer, questionId);
    },
    forwardAnswerChanged(answer, questionId) {
      this.$emit("AnswerChanged", answer, questionId);
    },
  },
};
</script>

<style scoped>
.title-bold {
  font-family: "OxaniumBold";
  font-weight: 700;
  font-size: 1.25rem;
  letter-spacing: -0.48px;
  margin-bottom: 10px;
}

.prompt-media-question {
  width: 100%;
}

.prompt-media {
  width: 100%;
  margin: 0 0 1.25rem;
  border-radius: 0.5rem;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.06);
  min-height: 120px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
}

.prompt-media::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 20px;
  pointer-events: none;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.07), transparent);
}

@media (min-width: 992px) {
  .prompt-media {
    min-height: 80px;
  }
}

.prompt-media--loading {
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.04) 25%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0.04) 75%
  );
  background-size: 200% 100%;
  animation: prompt-media-shimmer 1.2s ease-in-out infinite;
}

.prompt-media--fallback {
  background: rgba(0, 0, 0, 0.04);
  min-height: 180px;
}

.prompt-media--fallback::after {
  display: none;
}

.prompt-media__fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 180px;
}

.prompt-media__fallback-icon {
  width: 64px;
  height: 64px;
  color: #9aa3af;
}

.prompt-media__image {
  display: block;
  width: auto;
  max-width: 100%;
  max-height: 280px;
  object-fit: contain;
  object-position: center center;
}

@media (min-width: 992px) {
  .prompt-media__image {
    max-height: 290px;
  }

  .prompt-media__video,
  .prompt-media__youtube {
    max-height: 290px;
  }
}

.prompt-media__video-wrap {
  width: 100%;
  margin: 0 auto;
}

.prompt-media__video,
.prompt-media__youtube {
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  max-height: 280px;
  background-color: #000;
  background-position: center center;
  background-size: cover;
}

.prompt-media__youtube-player {
  width: 100%;
  height: 100%;
  position: relative;
  z-index: 1;
}

.prompt-media-question__input {
  margin-top: 0.75rem;
}

.prompt-media-question__list {
  margin-top: 0.75rem;
}

@keyframes prompt-media-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@media (max-width: 991px) {
  .prompt-media__video,
  .prompt-media__youtube {
    max-height: none;
  }
}
</style>
